import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Paper, Stack, Card, Stepper, Step, StepLabel, Avatar, Alert, Snackbar } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import TextField from '@mui/material/TextField';
import StarIcon from '@mui/icons-material/Star';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Fade } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import { Link } from 'react-router-dom';
import SEOForWebsite from '../SEOForWebsite';
import { submitForm } from '../../utils/formSubmission';

const brandBlue = '#0B60B1';

const steps = [
  {
    label: 'Comprehensive Audit',
    icon: <SecurityIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We analyze your entire email setup, authentication, and reputation to find every deliverability blocker.'
  },
  {
    label: 'Expert Fixes',
    icon: <BuildIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We implement technical fixes, optimize DNS, and resolve blacklist or spam issues.'
  },
  {
    label: 'Ongoing Monitoring',
    icon: <SpeedIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We monitor your sender reputation, inbox rates, and provide proactive alerts.'
  },
  {
    label: 'Human Support',
    icon: <EmailIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'You get real experts, not bots, for ongoing support and strategy.'
  }
];

const testimonials = [
  {
    name: 'Sarah L.',
    company: 'Growthly.io',
    quote: 'We went from 40% inbox to 98% in 2 weeks. The team at ImpactQuill is the real deal!',
    avatar: '/images/testimonial1.jpg',
    stars: 5
  },
  {
    name: 'James P.',
    company: 'FinTechMail',
    quote: 'No one else could solve our Gmail spam issue. These folks did it in days.',
    avatar: '/images/testimonial2.jpg',
    stars: 5
  },
  {
    name: 'Priya S.',
    company: 'EduReach',
    quote: 'Their audit was eye-opening. We fixed our DNS and saw instant results.',
    avatar: '/images/testimonial3.jpg',
    stars: 5
  },
  {
    name: 'Michael R.',
    company: 'TechFlow',
    quote: 'The team\'s expertise in email deliverability is unmatched. Our open rates increased by 40%.',
    avatar: '/images/testimonial4.jpg',
    stars: 5
  },
  {
    name: 'Lisa T.',
    company: 'MarketPro',
    quote: 'They helped us recover from a major blacklist issue. Professional and efficient service.',
    avatar: '/images/testimonial5.jpg',
    stars: 5
  },
  {
    name: 'David K.',
    company: 'CloudScale',
    quote: 'The ongoing monitoring service has been invaluable for our email campaigns.',
    avatar: '/images/testimonial6.jpg',
    stars: 5
  }
];

const faqs = [
  {
    q: 'What is email deliverability?',
    a: 'Email deliverability is the ability to deliver emails to recipients\' inboxes, not spam or junk folders. It depends on authentication, reputation, content, and technical setup.'
  },
  {
    q: 'How do you improve deliverability?',
    a: 'We audit your setup, fix authentication (SPF, DKIM, DMARC), monitor blacklists, optimize content, and provide ongoing support.'
  },
  {
    q: 'Can you help with Gmail/Outlook inboxing?',
    a: 'Yes! We specialize in improving inbox rates for all major providers, including Gmail, Outlook, Yahoo, and more.'
  },
  {
    q: 'Do you offer ongoing monitoring?',
    a: 'Yes, we offer ongoing monitoring, reporting, and support to keep your deliverability high.'
  }
];

const services = [
  'SPF, DKIM, DMARC Setup',
  'Blacklist Monitoring',
  'Inbox Placement Testing',
  'Bounce Analysis',
  'Domain & IP Reputation Checks',
  'Technical Infrastructure Audit',
  'Ongoing Deliverability Support',
  'Custom Deliverability Strategy',
  'ESP & CRM Integration',
  'Team Training & Best Practices'
];

const spamFactors = [
  {
    icon: <VerifiedUserIcon color="success" sx={{ fontSize: 36 }} />,
    title: 'SPF/DKIM/DMARC Setup',
    desc: 'Authenticate your emails to prevent spoofing and improve trust.'
  },
  {
    icon: <ReportGmailerrorredIcon color="error" sx={{ fontSize: 36 }} />,
    title: 'Blacklist Removal',
    desc: 'We help remove your domain/IP from major blacklists.'
  },
  {
    icon: <MarkEmailReadIcon color="primary" sx={{ fontSize: 36 }} />,
    title: 'Inbox Placement Testing',
    desc: 'Test and optimize to ensure your emails land in the inbox, not spam.'
  },
  {
    icon: <ListAltIcon color="secondary" sx={{ fontSize: 36 }} />,
    title: 'Content & Link Optimization',
    desc: 'We analyze and fix content, links, and formatting that trigger spam filters.'
  },
  {
    icon: <SpeedIcon color="info" sx={{ fontSize: 36 }} />,
    title: 'Reputation Monitoring',
    desc: 'Continuous monitoring of your sender reputation and proactive alerts.'
  }
];

export default function EmailDeliverabilityPage() {
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    domains: '',
    requestType: 'Audit',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused) {
        setTestimonialIdx((prev) => (prev + 1) % Math.ceil(testimonials.length / 3));
      }
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isPaused]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.email.trim()) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        name: formData.name,
        email: formData.email,
        domains: formData.domains,
        request_type: formData.requestType,
        message: formData.message,
        page_name: 'email_deliverability',
        request_for: 'Email Deliverability Service',
        source: 'email_deliverability_page',
        timestamp: new Date().toISOString()
      };

      await submitForm(submissionData, {
        formName: 'email_deliverability_contact',
        formSource: 'email_deliverability_page',
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: 'Thank you! We\'ll contact you soon about your email deliverability needs.',
            severity: 'success'
          });
          // Reset form
          setFormData({
            name: '',
            email: '',
            domains: '',
            requestType: 'Audit',
            message: ''
          });
        },
        onError: (error) => {
          console.error('Form submission error:', error);
          setSnackbar({
            open: true,
            message: 'Failed to submit. Please try again.',
            severity: 'error'
          });
        },
        onLoading: (loading) => {
          setIsSubmitting(loading);
        }
      });
      
    } catch (error) {
      console.error('Form submission error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', position: 'relative' }}>
      <SEOForWebsite 
        title="Email Deliverability Solutions - ImpactQuill"
        description="Enterprise email deliverability solutions ensuring your business-critical emails reach the inbox. Expert setup, monitoring, and support for SPF, DKIM, DMARC, and more."
        keywords="email deliverability, SPF, DKIM, DMARC, email authentication, inbox placement, blacklist monitoring"
        canonical="/services/email-deliverability"
      />
      {/* Hero Section with Animated SVG */}
      <Box sx={{ position: 'relative', bgcolor: brandBlue, color: '#fff', py: { xs: 10, md: 14 }, overflow: 'hidden', textAlign: 'center' }}>
        {/* Animated SVG background */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          <svg width="100%" height="100%" viewBox="0 0 1440 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0 }}>
            <path fill="#1976d2" fillOpacity="0.18" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,133.3C840,107,960,85,1080,101.3C1200,117,1320,171,1380,197.3L1440,224L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z" />
          </svg>
        </Box>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography variant="h2" fontWeight={900} gutterBottom sx={{ letterSpacing: '-1.5px', mb: 3, fontSize: { xs: 32, md: 48 } }}>
              Enterprise Email Deliverability Solutions
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, fontWeight: 400, opacity: 0.95, maxWidth: 700, mx: 'auto' }}>
              Ensure your business-critical emails always reach the inbox. Trusted by leading brands for advanced deliverability, compliance, and ongoing support.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 4 }}>
              <Button variant="contained" color="secondary" size="large" sx={{ fontWeight: 700, px: 5, boxShadow: 3, fontSize: 20, borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>
                Get Free Audit
              </Button>
              <Button variant="outlined" color="inherit" size="large" sx={{ fontWeight: 700, borderColor: '#fff', px: 5, fontSize: 20, borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>
                Talk to an Expert
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Features/Stepper Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{ mb: 4 }}>
              Your Deliverability Journey
            </Typography>
            <Stepper orientation="vertical" activeStep={-1} sx={{ background: 'transparent' }}>
              {steps.map((step) => (
                <Step key={step.label} completed={false} active={false}>
                  <StepLabel icon={step.icon}>
                    <Typography variant="h6" fontWeight={700}>{step.label}</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{step.desc}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: 'white', boxShadow: '0 8px 32px rgba(11,96,177,0.08)', maxWidth: 480, width: '100%', mx: 'auto' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom color="primary">Contact Our Experts</Typography>
              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onSubmit={handleFormSubmit}>
                <TextField label="Name" variant="outlined" size="small" fullWidth required value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} />
                <TextField label="Business Email" variant="outlined" size="small" fullWidth required type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} />
                <TextField label="Domains Having Issue (one per line)" variant="outlined" size="small" fullWidth multiline minRows={2} placeholder="example.com\nanotherdomain.com" value={formData.domains} onChange={(e) => handleInputChange('domains', e.target.value)} />
                <TextField 
                  label="Request Type" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  select 
                  value={formData.requestType} 
                  onChange={(e) => handleInputChange('requestType', e.target.value)}
                >
                  <MenuItem value="Audit">Audit</MenuItem>
                  <MenuItem value="Monitoring">Monitoring</MenuItem>
                  <MenuItem value="Management">Management</MenuItem>
                </TextField>
                <TextField label="Message" variant="outlined" size="small" fullWidth multiline minRows={3} value={formData.message} onChange={(e) => handleInputChange('message', e.target.value)} />
                <Button variant="contained" color="primary" type="submit" sx={{ fontWeight: 700, mt: 1, boxShadow: 2 }} disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Testimonial Carousel */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h5" fontWeight={800} textAlign="center" gutterBottom color="primary">
          Success Stories
        </Typography>
        <Box 
          sx={{ 
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              '& .MuiButton-root': {
                opacity: 1
              }
            }
          }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <Box sx={{ display: 'flex', transition: 'transform 0.5s ease', transform: `translateX(-${testimonialIdx * 100}%)` }}>
            {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, groupIndex) => (
              <Box key={groupIndex} sx={{ display: 'flex', gap: 3, minWidth: '100%', px: 2 }}>
                {testimonials.slice(groupIndex * 3, (groupIndex + 1) * 3).map((testimonial, index) => (
                  <Card 
                    key={index}
                    elevation={3} 
                    sx={{ 
                      p: 4, 
                      borderRadius: 3, 
                      flex: 1,
                      bgcolor: '#fff', 
                      transition: 'transform 0.2s', 
                      '&:hover': { 
                        transform: 'scale(1.04)', 
                        boxShadow: 8 
                      } 
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Avatar src={testimonial.avatar} alt={testimonial.name} sx={{ width: 48, height: 48 }} />
                      <Box>
                        <Typography fontWeight={700}>{testimonial.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{testimonial.company}</Typography>
                        <Box>
                          {[...Array(testimonial.stars)].map((_, i) => (
                            <StarIcon key={i} fontSize="small" color="warning" />
                          ))}
                        </Box>
                      </Box>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      " {testimonial.quote} "
                    </Typography>
                  </Card>
                ))}
              </Box>
            ))}
          </Box>
          <Button 
            onClick={() => setTestimonialIdx((prev) => (prev - 1 + Math.ceil(testimonials.length / 3)) % Math.ceil(testimonials.length / 3))} 
            sx={{ 
              position: 'absolute', 
              left: 0, 
              top: '50%', 
              transform: 'translateY(-50%)',
              opacity: 0,
              transition: 'opacity 0.2s',
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
            }}
          >
            &#8592;
          </Button>
          <Button 
            onClick={() => setTestimonialIdx((prev) => (prev + 1) % Math.ceil(testimonials.length / 3))} 
            sx={{ 
              position: 'absolute', 
              right: 0, 
              top: '50%', 
              transform: 'translateY(-50%)',
              opacity: 0,
              transition: 'opacity 0.2s',
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
            }}
          >
            &#8594;
          </Button>
        </Box>
      </Container>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {[
            { label: 'Domains Audited', value: '1,000+' },
            { label: 'Avg. Inbox Rate', value: '98%' },
            { label: 'Issues Resolved', value: '5,000+' },
            { label: 'Client Satisfaction', value: '99%' }
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Our Services
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Comprehensive deliverability consulting to ensure your emails land in the inbox
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 4 }}>
          {services.map((service, index) => (
            <Chip
              key={service}
              label={service}
              color={index % 2 === 0 ? 'primary' : 'secondary'}
              variant={index % 3 === 0 ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: 16, px: 2, py: 2, height: 'auto', minHeight: 40, borderRadius: 2, boxShadow: 1 }}
            />
          ))}
        </Box>
      </Container>

      {/* Spam Resolving Factors Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom color="primary">
          How We Resolve Spam Issues
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Our proven process tackles every factor that causes emails to land in spam.
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {spamFactors.map((factor, idx) => (
            <Grid item xs={12} sm={6} md={4} key={factor.title}>
              <Fade in timeout={600 + idx * 200}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-6px) scale(1.04)', boxShadow: 6 } }}>
                  <Box sx={{ mb: 2 }}>{factor.icon}</Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>{factor.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{factor.desc}</Typography>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section (Accordion) */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Stack spacing={3}>
          {faqs.map((faq) => (
            <Accordion key={faq.q} TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: 8, textAlign: 'center', mt: 8, position: 'relative' }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <VerifiedUserIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Box sx={{ bgcolor: '#fff', color: 'primary.main', px: 3, py: 1, borderRadius: 99, fontWeight: 700, fontSize: 18, boxShadow: 2, mb: 2, display: 'inline-block' }}>
              100% Satisfaction Guarantee
            </Box>
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>Ready to Improve Your Inbox Placement?</Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'rgba(255,255,255,0.85)' }}>
            Let our experts audit, fix, and support your email deliverability.
          </Typography>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large" 
            sx={{ fontWeight: 700, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}
            component={Link}
            to="/contact"
          >
            Start Your Project
          </Button>
        </Container>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 